/**
 * Stacked-surface detection for the native glass system. The native material
 * sits behind the whole webview, so a glass surface overlapping another shows
 * the lower surface's text sharp and unblurred ("bleed"). The collect pass
 * calls computeStackRelations on its z-sorted candidate list (bottom → top)
 * and marks upper surfaces with data-denker-stacked (CSS densify tint).
 * Popups follow the same rule wherever they sit: a menu over permanent
 * chrome, or drawn on its host's plate as a native child, is stacked too.
 * A menu, a glass frame (the summoned chat window), the input bar or an
 * agent cursor's bubble over solid card paper (wall cards, frame windows)
 * is stacked as well: the paper paints in the webview above every native
 * plate, so it would show sharp through the surface's see-through coat.
 * No Tauri imports.
 */
import type { SurfaceRole } from "@/components/ui/surface-contract";
import {
  isNativeGlassModelFrameGroup,
  readNativeGlassModelFrameStackScale,
} from "./native-glass-model-frames";
import { readSurfaceChain } from "./native-glass-stack";

export const STACKED_ATTR = "data-denker-stacked";

/**
 * Permanent floating chrome (space switcher, agent dock, input bar,
 * feedback/auth pills, sessions bubble) must keep a stable appearance —
 * canvas content sliding beneath them must not toggle the densify tint.
 * These roles never receive a stacked mark, and the only surface
 * they stack is a popup opened over them (isStackedPopupRole).
 *
 * The wall's full-window frost is exempt the same way: every surface sits
 * on it, so only a popup over it densifies.
 *
 * Exceptions: the canvas left rail and undocked agent status. Both need
 * overlap contrast above frame content. The cursor shares the menu's
 * occluding coat; the left rail uses the chrome densify tint.
 */
const STACK_EXEMPT_ROLES: ReadonlySet<SurfaceRole> = new Set([
  "rail",
  "toolbar",
  "backdrop",
]);

export const LEFT_RAIL_NATIVE_GROUP = "left-rail";
/** The agent cursor's bubble (`AgentStatusMaterial`). */
export const UNDOCKED_AGENT_STATUS_GROUP = "undocked-agent-status";
const STACK_PARTICIPATING_RAIL_GROUPS = new Set([
  LEFT_RAIL_NATIVE_GROUP,
  UNDOCKED_AGENT_STATUS_GROUP,
]);

const isStackExempt = (role: SurfaceRole, group?: string): boolean =>
  STACK_EXEMPT_ROLES.has(role) &&
  !(role === "rail" && STACK_PARTICIPATING_RAIL_GROUPS.has(group ?? ""));

/**
 * Menus, submenus and popovers become more solid over ANY glass beneath them:
 * permanent chrome included (it still never receives a mark itself), and a
 * popup drawn on its host's plate (native child) is on that glass by
 * construction.
 */
export const isStackedPopupRole = (role?: SurfaceRole): boolean =>
  role === "menu";

/** Rails that float over the wall and take the paper coat: the input bar
 * shell (the collect pass files it as a rail; a comment popover shares its
 * group as chrome and keeps its coat) and the agent cursor's bubble
 * (`AgentStatusMaterial`). */
const PAPER_COATED_RAIL_GROUPS = new Set([
  "input-bar",
  UNDOCKED_AGENT_STATUS_GROUP,
]);

/**
 * Menus, detached glass frames (the summoned chat window, cursor chats),
 * the summoned input bar and agent cursor bubbles take the opaque coat over
 * solid card paper: all draw above the workspace. Canvas frames (`frame:*`,
 * React Flow nodes) sit beneath any paper window, so paper over them never
 * thickens their coat. A cursor crossing card gaps keeps its coat through
 * the paper hold (`PAPER_DENSE_HOLD_MS`).
 */
const densifiesOverPaper = (rect: StackRect): boolean =>
  isStackedPopupRole(rect.role) ||
  (rect.role === "frame" && !isNativeGlassModelFrameGroup(rect.group)) ||
  (rect.role === "rail" && PAPER_COATED_RAIL_GROUPS.has(rect.group ?? ""));

/** The collapsed cursor count chip flies across the screen; passing under a
 * menu must not flip the menu's coat on and off. This only spares popups
 * above it: the chip paints its own readable fill (`.denker-compact-cursor-
 * count`), and the cursor's bubble is a stack-participating rail. */
const TRANSIENT_CHROME_GROUPS = new Set(["agent-cursor"]);

const isExemptRect = (rect: StackRect) =>
  rect.role !== undefined && isStackExempt(rect.role, rect.group);

const stacksPopupAbove = (lower: StackRect, upper: StackRect) =>
  isStackedPopupRole(upper.role) &&
  !TRANSIENT_CHROME_GROUPS.has(lower.group ?? "");

/** Surfaces kissing edges by less than this screen-space px don't stack. */
const MIN_OVERLAP_PX = 8;
/** Frame-frame overlap is judged in canvas-space px, not zoomed screen px. */
const MIN_FRAME_OVERLAP_CANVAS_PX = 8;

export type StackRect = {
  x: number;
  y: number;
  width: number;
  height: number;
  role?: SurfaceRole;
  group?: string;
  stackScale?: number;
  /** A plate at 0 (the wall's frost outside the Workspace) stacks nothing. */
  opacity?: number;
};

export type StackRelations = {
  stacked: Set<number>;
  /** Surfaces dense because they sit over card paper (a subset of `stacked`). */
  overPaper?: Set<number>;
};

/**
 * Once dense over card paper, a surface stays dense this long after the
 * overlap ends: card edges and gaps scrolling under the summoned chat
 * would otherwise switch its coat every frame.
 */
export const PAPER_DENSE_HOLD_MS = 300;
export type StackRelationOptions = {
  /**
   * Card paper under the popups and glass frames (`PAPER_SURFACE_SELECTOR`).
   * It paints inside the webview, above every native plate, so a surface
   * over it needs the opaque coat as over glass.
   */
  paperRects?: readonly StackRect[];
};

/**
 * Solid card paper: wall cards, Pinned tiles and focus or floating frame
 * windows. It draws no native plate, so the collect pass reads it only
 * while a popup or a glass frame shows.
 */
export const PAPER_SURFACE_SELECTOR = ".denker-solid-frame, .bg-card-surface";

const overlaps = (a: StackRect, b: StackRect, minOverlap: number) =>
  Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x) >= minOverlap &&
  Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y) >= minOverlap;

/**
 * Paper inside a glass menu or frame is that surface's own content; over
 * another glass surface's paper the glass-over-glass rule already applies.
 */
const GLASS_OWNER_SELECTOR = ".denker-glass-menu, .denker-glass-frame";

/**
 * The visible card paper under any of `rects`' popups, glass frames and the
 * summoned input bar; none is read while none shows, so scrolling a bare
 * wall measures no cards. The wall is virtualized, so a pass measures at
 * most its mounted cards.
 */
export const readPaperRects = (
  rects: readonly StackRect[],
  resolveStyle: (target: HTMLElement) => CSSStyleDeclaration,
): StackRect[] => {
  const above = rects.filter(densifiesOverPaper);
  if (above.length === 0) return [];
  const paper: StackRect[] = [];
  for (const element of document.querySelectorAll<HTMLElement>(
    PAPER_SURFACE_SELECTOR,
  )) {
    if (element.closest(GLASS_OWNER_SELECTOR)) continue;
    const { x, y, width, height } = element.getBoundingClientRect();
    const sheet = { x, y, width, height };
    if (!above.some((surface) => overlaps(surface, sheet, MIN_OVERLAP_PX))) {
      continue;
    }
    if (readSurfaceChain(element, resolveStyle).visible) paper.push(sheet);
  }
  return paper;
};

const isFrameStackRect = (rect: StackRect) =>
  rect.role === "frame" || isNativeGlassModelFrameGroup(rect.group);

const frameStackScale = (rect: StackRect) =>
  rect.stackScale ?? readNativeGlassModelFrameStackScale(rect.group) ?? 1;

const minOverlapForPair = (a: StackRect, b: StackRect) => {
  if (isFrameStackRect(a) && isFrameStackRect(b)) {
    return (
      MIN_FRAME_OVERLAP_CANVAS_PX *
      Math.max(frameStackScale(a), frameStackScale(b))
    );
  }
  return MIN_OVERLAP_PX;
};

/**
 * `rects` MUST be ordered bottom → top (the collect pass's sorted candidate
 * order is the single stacking authority). A surface over any lower one is
 * `stacked` (gets the densify tint). Stack-exempt chrome is never marked; it
 * only stacks a popup above it.
 */
export const computeStackRelations = (
  rects: readonly StackRect[],
  options: StackRelationOptions = {},
): StackRelations => {
  const stacked = new Set<number>();
  for (let upper = 1; upper < rects.length; upper += 1) {
    const a = rects[upper]!;
    if (isExemptRect(a)) continue;
    for (let lower = 0; lower < upper; lower += 1) {
      const b = rects[lower]!;
      if (b.opacity === 0) continue;
      if (isExemptRect(b) && !stacksPopupAbove(b, a)) continue;
      if (overlaps(a, b, minOverlapForPair(a, b))) {
        stacked.add(upper);
        break;
      }
    }
  }
  const paper = options.paperRects ?? [];
  const overPaper = new Set<number>();
  rects.forEach((rect, index) => {
    if (
      densifiesOverPaper(rect) &&
      paper.some((sheet) => overlaps(rect, sheet, MIN_OVERLAP_PX))
    ) {
      stacked.add(index);
      overPaper.add(index);
    }
  });
  return { stacked, overPaper };
};

/**
 * Reconcile data-denker-stacked with minimal writes: attribute writes
 * invalidate style (surface-system.css keys the densify tint on this mark),
 * so the steady state during a drag must perform zero writes.
 *
 * Elements that were stacked in a previous pass but are no longer in
 * `candidates` are handled by applyNativeSurfaceMarks in native-glass-collect
 * (the removal branch strips the attr along with the surface mark).
 */
export const syncStackMark = (element: HTMLElement, on: boolean) => {
  const isOn = element.getAttribute(STACKED_ATTR) === "true";
  if (on && !isOn) {
    element.setAttribute(STACKED_ATTR, "true");
  } else if (!on && element.hasAttribute(STACKED_ATTR)) {
    element.removeAttribute(STACKED_ATTR);
  }
};

/** When each surface was last dense over paper, and its pending release. */
const paperDenseAt = new WeakMap<HTMLElement, number>();
const paperReleases = new WeakMap<HTMLElement, number>();
/** Whether the latest pass stacked each surface. */
const passStacked = new WeakMap<HTMLElement, boolean>();

const cancelPaperRelease = (element: HTMLElement) => {
  const timer = paperReleases.get(element);
  if (timer === undefined) return;
  window.clearTimeout(timer);
  paperReleases.delete(element);
};

/** Ends a paper hold after `delay` unless a later pass stacked it again. */
const schedulePaperRelease = (element: HTMLElement, delay: number) => {
  cancelPaperRelease(element);
  paperReleases.set(
    element,
    window.setTimeout(() => {
      paperReleases.delete(element);
      if (!passStacked.get(element)) {
        syncStackMark(element, false);
      }
    }, delay),
  );
};

export const applyStackedMarks = (
  candidates: ReadonlyArray<{ element: HTMLElement }>,
  relations: StackRelations,
  now = performance.now(),
) => {
  candidates.forEach(({ element }, index) => {
    const stacked = relations.stacked.has(index);
    passStacked.set(element, stacked);
    if (relations.overPaper?.has(index)) paperDenseAt.set(element, now);
    const held =
      PAPER_DENSE_HOLD_MS -
      (now - (paperDenseAt.get(element) ?? Number.NEGATIVE_INFINITY));
    if (stacked || held <= 0) cancelPaperRelease(element);
    else schedulePaperRelease(element, held);
    syncStackMark(element, stacked || held > 0);
  });
};
